/**
 * AI Client Service
 * Cliente para comunicação com Supabase Edge Functions de IA
 */

import { supabase } from '@/integrations/supabase/client';
import type { AIRequest, AIResponse, AIStreamChunk } from './types';

/**
 * Cliente principal para requisições de IA
 */
export class AIClient {
  private static instance: AIClient | null = null;
  private activeController: AbortController | null = null;

  static getInstance(): AIClient {
    if (!AIClient.instance) {
      AIClient.instance = new AIClient();
    }
    return AIClient.instance;
  }

  /**
   * Cancelar requisição ativa (apenas quando explicitamente solicitado)
   */
  cancelActiveRequest(): void {
    if (this.activeController) {
      console.log('🚫 AI Request cancelada explicitamente');
      this.activeController.abort();
      this.activeController = null;
    }
  }

  /**
   * Fazer requisição para sugestão de IA
   */
  async suggest(request: AIRequest): Promise<AIResponse> {
    // Cancelar requisição anterior apenas se uma nova for iniciada
    if (this.activeController) {
      console.log('🔄 Cancelando requisição anterior para iniciar nova');
      this.activeController.abort();
    }
    
    this.activeController = new AbortController();
    
    console.log('🤖 Enviando requisição de sugestão:', {
      command: request.command,
      hasSelectedText: !!request.selectedText,
      contentLength: request.content?.length || 0,
      streaming: request.streaming || false
    });
    
    try {
      const { data, error } = await supabase.functions.invoke('ai-suggest', {
        body: request,
      });

      if (error) {
        console.error('❌ Erro na sugestão de IA:', error);
        return {
          success: false,
          content: '',
          error: error.message,
        };
      }

      return {
        success: true,
        content: data.content || '',
      };
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return {
          success: false,
          content: '',
          error: 'Requisição cancelada',
        };
      }
      
      console.error('❌ Erro na requisição:', error);
      return {
        success: false,
        content: '',
        error: error.message || 'Erro desconhecido',
      };
    } finally {
      this.activeController = null;
    }
  }

  /**
   * Fazer requisição para refatoração de IA
   */
  async refactor(request: AIRequest): Promise<AIResponse> {
    console.log('🔄 Enviando requisição de refatoração:', {
      command: request.command,
      hasSelectedText: !!request.selectedText,
      selectedTextLength: request.selectedText?.length || 0,
      options: request.options
    });
    
    try {
      const { data, error } = await supabase.functions.invoke('ai-refactor', {
        body: request,
      });

      if (error) {
        console.error('❌ Erro na refatoração de IA:', error);
        return {
          success: false,
          content: '',
          error: error.message,
        };
      }

      return {
        success: true,
        content: data.content || '',
      };
    } catch (error: any) {
      console.error('❌ Erro na requisição:', error);
      return {
        success: false,
        content: '',
        error: error.message || 'Erro desconhecido',
      };
    }
  }

  /**
   * Requisição streaming para IA
   * TODO: Implementar quando streaming real for necessário
   */
  async *streamRequest(request: AIRequest): AsyncGenerator<AIStreamChunk> {
    try {
      this.cancelActiveRequest();
      this.activeController = new AbortController();

      // Por enquanto, simula streaming dividindo a resposta
      const response = await this.suggest(request);
      
      if (response.success) {
        yield { type: 'start', content: '' };
        
        // Simula streaming dividindo o texto
        const words = response.content.split(' ');
        for (let i = 0; i < words.length; i++) {
          yield { 
            type: 'chunk', 
            content: words.slice(0, i + 1).join(' ') 
          };
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        
        yield { type: 'complete', content: response.content };
      } else {
        yield { 
          type: 'error', 
          content: '', 
          error: response.error 
        };
      }
    } catch (error) {
      yield { 
        type: 'error', 
        content: '', 
        error: error instanceof Error ? error.message : 'Erro desconhecido' 
      };
    }
  }
}

export const aiClient = AIClient.getInstance();